import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DestroyRef, Directive, inject, Input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import type { Subscription } from 'rxjs';
import { combineLatest, map, merge, of, startWith, take, throttleTime } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { CurrentUserSelectors } from '@core/account-data';
import { Analytics } from '@core/analytics-legacy';
import { filterBoolean } from '@core/common-legacy';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import {
  ComponentPositionPair,
  ComponentTooltipPosition,
  ComponentTooltipService,
  DialogOpenerService,
  OVERLAY_LEVEL,
  ResizeObserverEntryDirective,
} from '@core/user-interface-legacy';

import { GlobalProductUpdatesState } from './../../services/global-product-updates.state';
import { FeatureTooltipData, FeatureTooltipDirectiveConfig } from './../../types';
import { FeatureTooltipWrapperComponent } from './feature-tooltip-wrapper.component';

@Directive({
  selector: '[rtFeatureTooltip]',
  providers: [ComponentTooltipService],
  hostDirectives: [ResizeObserverEntryDirective],
  standalone: true,
  exportAs: 'rtFeatureTooltip',
})
export class FeatureTooltipWrapperDirective implements OnChanges, OnInit {
  private readonly positions = {
    [ComponentTooltipPosition.BottomLeft]: new ComponentPositionPair(
      { originX: 'start', originY: 'bottom' },
      { overlayX: 'start', overlayY: 'top' },
      'lefttop',
    ),
    [ComponentTooltipPosition.BottomRight]: new ComponentPositionPair(
      { originX: 'end', originY: 'bottom' },
      { overlayX: 'end', overlayY: 'top' },
      'righttop',
    ),
    [ComponentTooltipPosition.TopLeft]: new ComponentPositionPair(
      { originX: 'start', originY: 'top' },
      { overlayX: 'start', overlayY: 'bottom' },
      'leftbottom',
    ),
    [ComponentTooltipPosition.TopRight]: new ComponentPositionPair(
      { originX: 'end', originY: 'top' },
      { overlayX: 'end', overlayY: 'bottom' },
      'rightbottom',
    ),
    [ComponentTooltipPosition.Left]: new ComponentPositionPair(
      { originX: 'start', originY: 'center' },
      { overlayX: 'end', overlayY: 'center' },
      'right',
    ),
    [ComponentTooltipPosition.Right]: new ComponentPositionPair(
      { originX: 'end', originY: 'center' },
      { overlayX: 'start', overlayY: 'center' },
      'left',
    ),
  };

  @Input() public rtFeatureTooltip: FeatureTooltipDirectiveConfig;
  @Input('rtFeatureTooltipEnabled') public isForceEnabled: boolean;

  public rtFeatureTooltipDismissed = output<void>();
  public rtFeatureTooltipCtaClick = output<void>();
  public rtFeatureTooltipLearnMoreClick = output<void>();

  private destroyRef = inject(DestroyRef);
  private resizeDirective = inject(ResizeObserverEntryDirective);
  private overlayLevel = inject(OVERLAY_LEVEL);
  private openSubscription: Subscription | null = null;

  private globalProductUpdatesState = inject(GlobalProductUpdatesState);

  constructor(
    private componentTooltipService: ComponentTooltipService<
      typeof FeatureTooltipWrapperComponent,
      FeatureTooltipData
    >,
    private analytics: Analytics,
    private dialogService: DialogOpenerService,
    private store: Store,
  ) {
    this.componentTooltipService.setComponent(FeatureTooltipWrapperComponent);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.rtFeatureTooltip) {
      this.updateData(this.rtFeatureTooltip);
    }

    if (changes.isForceEnabled !== undefined && this.componentTooltipService.hasConfig()) {
      this.isForceEnabled ? this.open() : this.close();
    }
  }

  public ngOnInit(): void {
    this.updateData(this.rtFeatureTooltip);

    this.resizeDirective.resized
      .pipe(
        throttleTime(16, undefined, { leading: true, trailing: true }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (!this.componentTooltipService.isOpen()) return;

        this.componentTooltipService.updateTooltipPosition();
      });

    this.checkIsEnabled().subscribe(isEnabled => {
      if (this.isForceEnabled || isEnabled) {
        this.open();
      }
    });
  }

  public close(): void {
    this.openSubscription?.unsubscribe();
    this.openSubscription = null;
    this.componentTooltipService.closeTooltip();
  }

  private open() {
    if (!this.globalProductUpdatesState.enabled) return;

    this.openSubscription?.unsubscribe();
    this.openSubscription = merge(
      ...this.dialogService.openDialogs.map(dialogRef => dialogRef.afterClosed),
    )
      .pipe(
        startWith(null),
        map(() => this.canOpenTooltip()),
        filterBoolean,
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.openTooltip();
        this.openSubscription = null;
      });
  }

  private updateData(config: FeatureTooltipDirectiveConfig) {
    this.componentTooltipService.setConfig({
      positions: config.positions || this.positions,
      overlayClipArea: config.overlayClipArea,
      hasBackdrop: false,
      ...(config.observeOnElement ? { observeOnElement: config.observeOnElement } : {}),
      ...(config.observeOnCustomElement
        ? { observeOnCustomElement: config.observeOnCustomElement }
        : {}),
      withFallbackPoint: true,
      withPush: config.withPush,
    });
    this.componentTooltipService.setComponentData(this.rtFeatureTooltip);
  }

  private canOpenTooltip() {
    const maxLevel = Math.max(
      ...this.dialogService.openDialogs.map(dialogRef => dialogRef.containerInstance.overlayLevel),
    );
    return maxLevel <= this.overlayLevel;
  }

  private openTooltip(): void {
    if (this.componentTooltipService.isOpen()) return;

    const { instance, changeDetectorRef } = this.componentTooltipService.openTooltip();
    changeDetectorRef.detectChanges();

    instance.close$.pipe(takeUntil(instance.ngOnDestroy$)).subscribe(() => {
      this.tooltipDismissed();
      this.close();
    });

    instance.ctaClick$.pipe(takeUntil(instance.ngOnDestroy$)).subscribe(() => {
      this.tooltipDismissed();
      this.rtFeatureTooltipCtaClick.emit();
      this.close();
    });

    instance.learnMoreClick$.pipe(takeUntil(instance.ngOnDestroy$)).subscribe(() => {
      this.analytics.track(`[Feature tooltip] Learn more ${this.rtFeatureTooltip.title}`);
      this.rtFeatureTooltipLearnMoreClick.emit();
    });
  }

  private checkIsEnabled() {
    const featureDiscoveryFlagUsed$ = this.rtFeatureTooltip.flagName
      ? this.store.select(
          CurrentUserSelectors.featureDiscovery({ feature: this.rtFeatureTooltip.flagName }),
        )
      : of(true);

    const isForceEnabled$ = this.isForceEnabled !== undefined ? of(this.isForceEnabled) : of(true);

    const hasAbilities$ = this.store.select(
      AbilitiesSelectors.hasMany(this.rtFeatureTooltip.requiredAbilities ?? []),
    );

    return combineLatest([featureDiscoveryFlagUsed$, hasAbilities$, isForceEnabled$]).pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
      map(
        ([featureDiscoveryFlagUsed, hasAbilities, isForceEnabled]) =>
          !featureDiscoveryFlagUsed &&
          hasAbilities &&
          !this.globalProductUpdatesState.enabled &&
          isForceEnabled,
      ),
    );
  }

  private tooltipDismissed(): void {
    if (this.rtFeatureTooltip.flagName) {
      this.store.dispatch(
        FeatureDiscoveryActions.update({ payload: { [this.rtFeatureTooltip.flagName]: true } }),
      );
    }

    this.rtFeatureTooltipDismissed.emit();
  }
}
